import { API_BASE_ENDPOINT } from '@/api/constants';
import { ENDPOINTS } from '@/api/endpoints';

import type { TelehealthReportData } from './types';

/**
 * Fetches telehealth report data using a short-lived print token.
 * Uses a standalone fetch instead of the app's GET helper because
 * the standard FETCH calls logout() on 401/403, which redirects the
 * headless browser to the login page and breaks PDF generation.
 */
export async function getTelehealthReportData(
  gradeId: string,
  token: string,
  enableInsights: boolean
): Promise<TelehealthReportData> {
  const endpoint = ENDPOINTS.GRADE_TELEHEALTH_REPORT_DATA.replace(
    '{grade_id}',
    encodeURIComponent(gradeId)
  );

  const params = new URLSearchParams({
    token,
    enable_insights: String(enableInsights),
  });

  const url = `${API_BASE_ENDPOINT}${endpoint}?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({ detail: response.statusText }))) as {
      detail?: string;
      message?: string;
    };
    throw new Error(errorBody.detail ?? errorBody.message ?? `HTTP ${response.status}`);
  }

  const body = (await response.json()) as {
    status: string;
    message: string;
    data: TelehealthReportData;
  };

  return body.data;
}
